<script lang="ts">
  // Snooze sheet (spec §15): three presets, then a custom picker with 14 day chips and the
  // 08 / 12 / 17 / 20 time chips (today keeps only times > now + 5 min, else `snoozeNoTime`).
  import { formatPickerDate, t } from '$lib/i18n';
  import { pickerAt, pickerDays, pickerTimes } from '$lib/mail/snooze';
  import { store } from '$lib/store';
  import Icon from '../ui/Icon.svelte';
  import Sheet from '../ui/Sheet.svelte';

  let customOpen = $state(false);
  let pickDay = $state<number | null>(null);
  let pickHour = $state<number | null>(null);

  const target = $derived(store.snoozeTarget);
  const presets = $derived(target ? store.snoozeOptions() : []);
  const days = $derived(target ? pickerDays() : []);
  const times = $derived(pickDay === null ? [] : pickerTimes(days[pickDay]!));
  const at = $derived(pickDay !== null && pickHour !== null ? pickerAt(days[pickDay]!, pickHour) : null);

  function dayLabel(i: number, d: Date): string {
    if (i === 0) return t('snoozeToday');
    if (i === 1) return t('snoozeTomorrowDay');
    return formatPickerDate(d);
  }
  const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

  function close() {
    store.snoozeTarget = null;
  }
  // Reset the picker whenever the sheet closes.
  $effect(() => {
    if (!target) {
      customOpen = false;
      pickDay = null;
      pickHour = null;
    }
  });

  async function snoozeAt(ms: number) {
    const uids = target;
    if (!uids) return;
    const ok = await store.snooze([...uids], ms);
    if (ok) close();
  }
  function pickDayAt(i: number) {
    pickDay = i;
    if (pickHour !== null && !pickerTimes(days[i]!).includes(pickHour)) pickHour = null;
  }
</script>

<Sheet label={t('snooze')} cls="snooze-sheet" onclose={close}>
  {#if !customOpen}
    {#each presets as p (p.key)}
      <button type="button" class="fbtn big snz-opt" data-key={p.key} onclick={() => void snoozeAt(p.at)}>
        <span class="fi"><Icon name="moon" size={20} /></span><span class="fn">{t(p.key)}</span>
      </button>
    {/each}
    <div class="sheet-sep"></div>
    <button type="button" class="fbtn big snz-opt snz-more" data-key="custom" onclick={() => (customOpen = true)}>
      <span class="fi"><Icon name="sort" size={20} /></span><span class="fn">{t('snoozeCustom')}</span>
    </button>
  {:else}
    <div class="snz-pick">
      <div class="snz-h">{t('snoozePickDay')}</div>
      <div class="snz-row">
        {#each days as d, i (d.getTime())}
          <button type="button" class="snz-chip" class:on={pickDay === i} aria-pressed={pickDay === i} onclick={() => pickDayAt(i)}>{dayLabel(i, d)}</button>
        {/each}
      </div>
      <div class="snz-h">{t('snoozePickTime')}</div>
      {#if pickDay === null || times.length}
        <div class="snz-row">
          {#each times as h (h)}
            <button type="button" class="snz-chip" class:on={pickHour === h} aria-pressed={pickHour === h} onclick={() => (pickHour = h)}>{hh(h)}</button>
          {/each}
        </div>
      {:else}
        <p class="snz-none">{t('snoozeNoTime')}</p>
      {/if}
      <button type="button" class="snz-go" disabled={at === null} onclick={() => at !== null && void snoozeAt(at)}>
        {t('snoozeConfirm')}{#if at !== null}&nbsp;{formatPickerDate(at, true)}{/if}
      </button>
    </div>
  {/if}
</Sheet>

<style>
  /* @css shell/SnoozeSheet start */
  .fbtn {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-block: var(--space-3);
    padding-inline: var(--space-3);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-control);
    color: var(--text-body);
    width: 100%;
    text-align: start;
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .fbtn:hover {
    background: var(--surface-sunken)
  }

  .fbtn.big {
    padding: var(--space-3) var(--space-4);
    min-block-size: var(--control-lg)
  }

  .sheet-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-2) var(--space-3)
  }

  .snz-pick {
    display: grid;
    gap: var(--space-2);
    padding: 0 var(--space-2) var(--space-2)
  }

  .snz-h {
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium)
  }

  .snz-row {
    display: flex;
    gap: var(--space-2);
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px
  }

  .snz-row::-webkit-scrollbar {
    display: none
  }

  .snz-chip {
    flex: none;
    min-height: 44px;
    padding-inline: var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-body);
    font-size: var(--text-sm);
    white-space: nowrap
  }

  .snz-chip:hover {
    background: var(--surface-sunken)
  }

  .snz-chip.on {
    background: var(--mail-selected-bg);
    color: var(--mail-selected-fg);
    border-color: var(--border-accent);
    font-weight: var(--weight-medium)
  }

  .snz-none {
    color: var(--text-muted);
    font-size: var(--text-sm);
    margin: 0
  }

  .snz-go {
    min-block-size: var(--control-lg);
    margin-top: var(--space-2);
    border-radius: var(--radius-control);
    background: var(--mail-accent);
    color: var(--text-on-accent);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm)
  }

  .snz-go:hover:not(:disabled) {
    background: var(--mail-accent-hover)
  }

  .snz-go:disabled {
    opacity: .5;
    cursor: default
  }

  .fi {
    width: var(--control-xs);
    display: grid;
    place-items: center
  }

  .fn {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .fbtn:active,
  .snz-chip:active {
    background: var(--mail-pressed)
  }
  /* @css shell/SnoozeSheet end */
</style>
